import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './AdminPage.css';

const API_BASE = `${process.env.REACT_APP_API_URL}/admin`;

function AdminPage() {
  const navigate = useNavigate();
  const [authenticated, setAuthenticated] = useState(false);
  const [token, setToken] = useState('');
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isDark, setIsDark] = useState(() => {
    return localStorage.getItem('adminTheme') === 'dark' ||
      document.documentElement.getAttribute('data-theme') === 'dark';
  });

  // ✅ NEW: Suspend modal state
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [suspendTarget, setSuspendTarget] = useState(null);
  const [suspendReason, setSuspendReason] = useState('');
  const [suspendActionLoading, setSuspendActionLoading] = useState(false);
  const [suspendModalError, setSuspendModalError] = useState('');

  // ✅ NEW: Delete modal state
  const [showDeleteUserModal, setShowDeleteUserModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteActionLoading, setDeleteActionLoading] = useState(false);
  const [deleteModalError, setDeleteModalError] = useState('');

  // ✅ NEW: Per-row action loading (for unsuspend, which has no modal)
  const [rowActionLoading, setRowActionLoading] = useState(null);

  useEffect(() => {
    const savedToken = sessionStorage.getItem('adminToken');
    if (savedToken) { setToken(savedToken); setAuthenticated(true); }
  }, []);

  useEffect(() => {
    if (authenticated) fetchDashboard();
  }, [authenticated]);

  useEffect(() => {
    if (!authenticated) return;
    if (activeTab === 'dashboard') fetchDashboard();
    if (activeTab === 'subscribers') fetchSubscribers();
    if (activeTab === 'users') fetchUsers();
  }, [activeTab, authenticated]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
    localStorage.setItem('adminTheme', isDark ? 'dark' : 'light');
  }, [isDark]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const response = await fetch(`${API_BASE}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginForm)
      });
      const data = await response.json();
      if (data.success) {
        setToken(data.token);
        setAuthenticated(true);
        sessionStorage.setItem('adminToken', data.token);
      } else {
        setLoginError(data.message || 'Invalid credentials');
      }
    } catch (err) {
      setLoginError('Connection error. Is the backend running?');
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminToken');
    setAuthenticated(false);
    setToken('');
    setDashboard(null);
    setSubscribers([]);
    setUsers([]);
  };

  const fetchDashboard = async () => {
    setLoading(true); setError('');
    try {
      const response = await fetch(`${API_BASE}/dashboard`, { headers: { 'Admin-Token': token } });
      const data = await response.json();
      if (response.ok) setDashboard(data);
      else setError(data.error || 'Failed to load dashboard');
    } catch (err) { setError('Failed to connect to backend'); }
    finally { setLoading(false); }
  };

  const fetchSubscribers = async () => {
    setLoading(true); setError('');
    try {
      const response = await fetch(`${API_BASE}/subscribers`, { headers: { 'Admin-Token': token } });
      const data = await response.json();
      if (response.ok) setSubscribers(data);
      else setError(data.error || 'Failed to load subscribers');
    } catch (err) { setError('Failed to connect to backend'); }
    finally { setLoading(false); }
  };

  const fetchUsers = async () => {
    setLoading(true); setError('');
    try {
      const response = await fetch(`${API_BASE}/users`, { headers: { 'Admin-Token': token } });
      const data = await response.json();
      if (response.ok) setUsers(data);
      else setError(data.error || 'Failed to load users');
    } catch (err) { setError('Failed to connect to backend'); }
    finally { setLoading(false); }
  };

  const getPlanBadge = (plan) => {
    if (plan === 'PREMIUM') return <span className="badge badge-premium">⭐ Premium</span>;
    return <span className="badge badge-free">🆓 Free</span>;
  };

  const getStatusBadge = (status) => {
    if (status === 'ACTIVE') return <span className="badge badge-active">Active</span>;
    if (status === 'PENDING') return <span className="badge badge-pending">Pending</span>;
    if (status === 'EXPIRED') return <span className="badge badge-expired">Expired</span>;
    if (status === 'FAILED') return <span className="badge badge-failed">Failed</span>;
    return <span className="badge">{status}</span>;
  };

  // ✅ NEW: Badge specifically for account status (ACTIVE / SUSPENDED)
  const getAccountStatusBadge = (accountStatus) => {
    if (accountStatus === 'SUSPENDED') return <span className="badge badge-suspended">🚫 Suspended</span>;
    return <span className="badge badge-account-active">✅ Active</span>;
  };

  // ✅ NEW: Suspend modal handlers
  const openSuspendModal = (username) => {
    setSuspendTarget(username);
    setSuspendReason('');
    setSuspendModalError('');
    setShowSuspendModal(true);
  };

  const closeSuspendModal = () => {
    if (suspendActionLoading) return;
    setShowSuspendModal(false);
    setSuspendTarget(null);
    setSuspendReason('');
    setSuspendModalError('');
  };

  const confirmSuspend = async () => {
    if (!suspendTarget) return;
    setSuspendActionLoading(true);
    setSuspendModalError('');
    try {
      const response = await fetch(`${API_BASE}/users/${suspendTarget}/suspend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Admin-Token': token },
        body: JSON.stringify({ reason: suspendReason })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setShowSuspendModal(false);
        setSuspendTarget(null);
        setSuspendReason('');
        fetchUsers();
      } else {
        setSuspendModalError(data.message || 'Failed to suspend user');
      }
    } catch (err) {
      setSuspendModalError('Connection error. Is the backend running?');
    } finally {
      setSuspendActionLoading(false);
    }
  };

  // ✅ NEW: Unsuspend (no modal — direct action)
  const handleUnsuspend = async (username) => {
    setRowActionLoading(username);
    setError('');
    try {
      const response = await fetch(`${API_BASE}/users/${username}/unsuspend`, {
        method: 'POST',
        headers: { 'Admin-Token': token }
      });
      const data = await response.json();
      if (response.ok && data.success) {
        fetchUsers();
      } else {
        setError(data.message || 'Failed to unsuspend user');
      }
    } catch (err) {
      setError('Failed to connect to backend');
    } finally {
      setRowActionLoading(null);
    }
  };

  // ✅ NEW: Delete modal handlers
  const openDeleteUserModal = (username) => {
    setDeleteTarget(username);
    setDeleteModalError('');
    setShowDeleteUserModal(true);
  };

  const closeDeleteUserModal = () => {
    if (deleteActionLoading) return;
    setShowDeleteUserModal(false);
    setDeleteTarget(null);
    setDeleteModalError('');
  };

  const confirmDeleteUser = async () => {
    if (!deleteTarget) return;
    setDeleteActionLoading(true);
    setDeleteModalError('');
    try {
      const response = await fetch(`${API_BASE}/users/${deleteTarget}`, {
        method: 'DELETE',
        headers: { 'Admin-Token': token }
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setShowDeleteUserModal(false);
        // Remove the deleted user from the list immediately
        setUsers((prev) => prev.filter((u) => u.username !== deleteTarget));
        setDeleteTarget(null);
      } else {
        setDeleteModalError(data.message || 'Failed to delete user');
      }
    } catch (err) {
      setDeleteModalError('Connection error. Is the backend running?');
    } finally {
      setDeleteActionLoading(false);
    }
  };

  if (!authenticated) {
    return (
      <div className="admin-login-container">
        <div className="admin-login-card">
          <div className="admin-theme-toggle-login">
            <button className="admin-theme-btn" onClick={() => setIsDark(!isDark)} title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}>
              {isDark ? '☀️' : '🌙'}
            </button>
          </div>
          <div className="admin-login-icon">🔐</div>
          <h1>Admin Access</h1>
          <p>Couple Check Administration Panel</p>
          {loginError && <div className="admin-error">⚠️ {loginError}</div>}
          <form onSubmit={handleLogin} className="admin-login-form">
            <div className="admin-form-group">
              <label>Username</label>
              <input type="text" value={loginForm.username} onChange={e => setLoginForm({ ...loginForm, username: e.target.value })} placeholder="Admin username" required />
            </div>
            <div className="admin-form-group">
              <label>Password</label>
              <input type="password" value={loginForm.password} onChange={e => setLoginForm({ ...loginForm, password: e.target.value })} placeholder="Admin password" required />
            </div>
            <button type="submit" className="admin-login-btn">Login to Admin Panel</button>
          </form>
          <button className="admin-back-btn" onClick={() => navigate('/')}>← Back to App</button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <div className="admin-header-left">
          <h1>🔐 Admin Panel</h1>
          <p>Couple Check Administration</p>
        </div>
        <div className="admin-header-right">
          <button className="admin-theme-btn" onClick={() => setIsDark(!isDark)} title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}>
            {isDark ? '☀️' : '🌙'}
          </button>
          <button className="admin-logout-btn" onClick={handleLogout}>Logout</button>
        </div>
      </div>

      <div className="admin-tabs">
        <button className={`admin-tab ${activeTab === 'dashboard' ? 'active' : ''}`} onClick={() => setActiveTab('dashboard')}>📊 Dashboard</button>
        <button className={`admin-tab ${activeTab === 'subscribers' ? 'active' : ''}`} onClick={() => setActiveTab('subscribers')}>⭐ Subscribers</button>
        <button className={`admin-tab ${activeTab === 'users' ? 'active' : ''}`} onClick={() => setActiveTab('users')}>👥 All Users</button>
      </div>

      {error && <div className="admin-error">⚠️ {error}</div>}
      {loading && <div className="admin-loading">Loading...</div>}

      {activeTab === 'dashboard' && dashboard && !loading && (
        <div className="admin-dashboard">
          <div className="admin-stats-grid">
            <div className="admin-stat-card total"><div className="stat-icon">👥</div><div className="stat-content"><h3>Total Users</h3><p className="stat-value">{dashboard.totalUsers}</p></div></div>
            <div className="admin-stat-card premium"><div className="stat-icon">⭐</div><div className="stat-content"><h3>Premium Users</h3><p className="stat-value">{dashboard.premiumUsers}</p></div></div>
            <div className="admin-stat-card free"><div className="stat-icon">🆓</div><div className="stat-content"><h3>Free Users</h3><p className="stat-value">{dashboard.freeUsers}</p></div></div>
            <div className="admin-stat-card revenue"><div className="stat-icon">💰</div><div className="stat-content"><h3>Total Revenue</h3><p className="stat-value">KES {dashboard.totalRevenue?.toLocaleString()}</p></div></div>
            <div className="admin-stat-card expiring"><div className="stat-icon">⏳</div><div className="stat-content"><h3>Expiring (7 days)</h3><p className="stat-value">{dashboard.expiringSoon}</p></div></div>
            <div className="admin-stat-card expired"><div className="stat-icon">❌</div><div className="stat-content"><h3>Expired/Failed</h3><p className="stat-value">{dashboard.expiredUsers}</p></div></div>
          </div>
          <p className="admin-generated-at">Last updated: {dashboard.generatedAt}</p>
        </div>
      )}

      {activeTab === 'subscribers' && !loading && (
        <div className="admin-table-container">
          <div className="admin-table-header"><h2>⭐ Subscribers ({subscribers.length})</h2></div>
          {subscribers.length === 0 ? (
            <div className="admin-empty">No subscribers yet.</div>
          ) : (
            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead><tr><th>Username</th><th>Plan</th><th>Status</th><th>Amount</th><th>Phone</th><th>M-Pesa Ref</th><th>Start Date</th><th>End Date</th></tr></thead>
                <tbody>
                  {subscribers.map((sub, index) => (
                    <tr key={index}>
                      <td className="username-cell">{sub.username}</td>
                      <td>{getPlanBadge(sub.plan)}</td>
                      <td>{getStatusBadge(sub.status)}</td>
                      <td>KES {sub.amount}</td>
                      <td>{sub.phoneNumber}</td>
                      <td className="ref-cell">{sub.paymentReference}</td>
                      <td>{sub.startDate}</td>
                      <td>{sub.endDate}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'users' && !loading && (
        <div className="admin-table-container">
          <div className="admin-table-header"><h2>👥 All Users ({users.length})</h2></div>
          {users.length === 0 ? (
            <div className="admin-empty">No users found.</div>
          ) : (
            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead><tr><th>Username</th><th>Plan</th><th>Subscription Status</th><th>Account Status</th><th>Expires</th><th>Joined</th><th>Actions</th></tr></thead>
                <tbody>
                  {users.map((user, index) => (
                    <tr key={index}>
                      <td className="username-cell">{user.username}</td>
                      <td>{getPlanBadge(user.plan)}</td>
                      <td>{getStatusBadge(user.subscriptionStatus)}</td>
                      <td>{getAccountStatusBadge(user.accountStatus)}</td>
                      <td>{user.endDate}</td>
                      <td>{user.createdAt?.split('T')[0]}</td>
                      <td className="admin-actions-cell">
                        {user.accountStatus === 'SUSPENDED' ? (
                          <button
                            className="admin-action-btn unsuspend-btn"
                            onClick={() => handleUnsuspend(user.username)}
                            disabled={rowActionLoading === user.username}
                          >
                            {rowActionLoading === user.username ? '⏳' : '✅ Unsuspend'}
                          </button>
                        ) : (
                          <button
                            className="admin-action-btn suspend-btn"
                            onClick={() => openSuspendModal(user.username)}
                          >
                            🚫 Suspend
                          </button>
                        )}
                        <button
                          className="admin-action-btn delete-user-btn"
                          onClick={() => openDeleteUserModal(user.username)}
                        >
                          🗑️ Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ✅ NEW: Suspend confirmation modal */}
      {showSuspendModal && (
        <div className="admin-modal-overlay" onClick={closeSuspendModal}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="admin-modal-title">🚫 Suspend User</h2>
            <p className="admin-modal-text">
              You are about to suspend <strong>{suspendTarget}</strong>. They will be blocked from logging in
              and will receive a notification with the reason below (optional).
            </p>
            <label className="admin-modal-label">Reason (optional)</label>
            <textarea
              className="admin-modal-textarea"
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              placeholder="e.g. Violation of terms of service"
              rows="3"
              disabled={suspendActionLoading}
            />
            {suspendModalError && <div className="admin-modal-error">⚠️ {suspendModalError}</div>}
            <div className="admin-modal-actions">
              <button className="admin-modal-cancel" onClick={closeSuspendModal} disabled={suspendActionLoading}>
                Cancel
              </button>
              <button className="admin-modal-confirm-suspend" onClick={confirmSuspend} disabled={suspendActionLoading}>
                {suspendActionLoading ? '⏳ Suspending...' : 'Suspend User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ NEW: Delete user confirmation modal */}
      {showDeleteUserModal && (
        <div className="admin-modal-overlay" onClick={closeDeleteUserModal}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="admin-modal-title">🗑️ Delete User</h2>
            <p className="admin-modal-text">
              This will <strong>permanently delete</strong> the account <strong>{deleteTarget}</strong> and all
              their entries, profile, subscription history, and notifications. This cannot be undone.
            </p>
            {deleteModalError && <div className="admin-modal-error">⚠️ {deleteModalError}</div>}
            <div className="admin-modal-actions">
              <button className="admin-modal-cancel" onClick={closeDeleteUserModal} disabled={deleteActionLoading}>
                Cancel
              </button>
              <button className="admin-modal-confirm-delete" onClick={confirmDeleteUser} disabled={deleteActionLoading}>
                {deleteActionLoading ? '⏳ Deleting...' : 'Permanently Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPage;